<script setup lang="ts">
/**
 * ThePublicHeader - навигация публичной зоны.
 * Sticky, прозрачная при скролле наверх, с фоном при скролле вниз.
 * Мобильный drawer-меню.
 */
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const mobileMenuOpen = ref(false)
const scrolled = ref(false)

function onScroll() {
  scrolled.value = window.scrollY > 20
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

interface NavLink {
  key: string
  label: string
  path: string
}

const navLinks: NavLink[] = [
  { key: 'home', label: 'public.header.nav.home', path: '/' },
  { key: 'screener', label: 'public.header.nav.screener', path: '/screener' },
  { key: 'about', label: 'public.header.nav.about', path: '/about' },
  { key: 'features', label: 'public.header.nav.features', path: '/features' },
  { key: 'pricing', label: 'public.header.nav.pricing', path: '/pricing' },
  { key: 'blog', label: 'public.header.nav.blog', path: '/blog' },
]
</script>

<template>
  <header
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    :class="scrolled ? 'bg-base-100/95 backdrop-blur-md border-b border-primary/5 shadow-sm' : 'bg-transparent'"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
      <div class="flex items-center justify-between h-16">
        <!-- Логотип -->
        <RouterLink
          to="/"
          class="flex items-center gap-2 shrink-0"
        >
          <img
            src="/logo_altermex.png"
            alt="AlterMex"
            class="w-8 h-8"
          >
          <span class="text-brand text-lg">AlterMex</span>
        </RouterLink>

        <!-- Навигация (desktop) -->
        <nav class="hidden lg:flex items-center gap-1">
          <RouterLink
            v-for="link in navLinks"
            :key="link.key"
            :to="link.path"
            class="px-4 py-2 text-sm font-medium text-base-content/60 hover:text-base-content transition-colors rounded-lg hover:bg-base-content/5"
            active-class="!text-primary"
          >
            {{ t(link.label) }}
          </RouterLink>
        </nav>

        <!-- CTA кнопки (desktop) -->
        <div class="hidden lg:flex items-center gap-3">
          <RouterLink
            to="/login"
            class="btn btn-ghost btn-sm text-base-content/70 hover:text-base-content"
          >
            {{ t('public.header.cta.login') }}
          </RouterLink>
          <RouterLink
            to="/register"
            class="btn btn-primary btn-sm"
          >
            {{ t('public.header.cta.start') }}
          </RouterLink>
        </div>

        <!-- Мобильная кнопка меню -->
        <button
          class="btn btn-ghost btn-sm btn-square lg:hidden"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <svg
            v-if="!mobileMenuOpen"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Мобильное меню -->
    <Transition name="slide-down">
      <div
        v-if="mobileMenuOpen"
        class="lg:hidden bg-base-100/95 backdrop-blur-md border-b border-primary/5"
      >
        <div class="container mx-auto px-4 py-4 space-y-1">
          <RouterLink
            v-for="link in navLinks"
            :key="link.key"
            :to="link.path"
            class="block px-4 py-3 text-sm font-medium text-base-content/60 hover:text-base-content hover:bg-base-content/5 rounded-lg transition-colors"
            active-class="!text-primary !bg-primary/5"
            @click="mobileMenuOpen = false"
          >
            {{ t(link.label) }}
          </RouterLink>
          <div class="pt-3 border-t border-primary/5 flex flex-col gap-2">
            <RouterLink
              to="/login"
              class="btn btn-ghost btn-sm w-full"
              @click="mobileMenuOpen = false"
            >
              {{ t('public.header.cta.login') }}
            </RouterLink>
            <RouterLink
              to="/register"
              class="btn btn-primary btn-sm w-full"
              @click="mobileMenuOpen = false"
            >
              {{ t('public.header.cta.start') }}
            </RouterLink>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
